/**
 * The composer's attachment list, and the uploads behind it.
 *
 * Kept out of `Composer.tsx` because it is a state machine, not layout: a file
 * picked here is uploading, uploaded or refused, it can be removed at any of
 * those three points, and the window it lives in is being resized, minimised
 * and typed into the whole time. Mixing that into the component that draws the
 * window is how the "remove" button ends up deleting the wrong row.
 *
 * The upload starts the moment a file is picked, not when Send is pressed. An
 * operator attaches five files and then writes for ten minutes; a Send that had
 * to move 20MB first would sit there looking broken for a minute, and the mail
 * would be no faster for it.
 *
 * Every row keeps a local id for the whole of its life. The server's id only
 * arrives when the upload finishes, so keying the list on it would leave every
 * in-flight row sharing the key `undefined` - React would then reuse one row's
 * DOM for another's, and a spinner would appear to jump between files.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  attachFromMedia,
  deleteAttachment,
  getAttachmentLimits,
  uploadAttachment,
  type AttachmentLimits,
} from "../../../../lib/mail";

/** One file in the composer's tray, at whatever stage it has reached. */
export interface StagedFile {
  /** Stable for the row's whole life, unlike `id`. */
  localId: string;
  name: string;
  size: number;
  /** The server's id, once the upload has finished. This is what a send names. */
  id?: string;
  status: "uploading" | "done" | "error";
  /** An error CODE, translated at the point it is drawn. */
  error?: string;
}

/**
 * The caps, until the server says otherwise.
 *
 * Only a starting point - `getAttachmentLimits` replaces them on mount. They
 * exist so the very first pick, in the moment before that answer lands, is
 * checked against something rather than waved through.
 */
const FALLBACK_LIMITS: AttachmentLimits = {
  maxBytes: 15 * 1024 * 1024,
  maxTotalBytes: 20 * 1024 * 1024,
  maxFiles: 10,
  // Empty rather than a guessed list: the server refuses these anyway, and a
  // stale local copy would refuse a file the mailbox would actually have sent.
  blockedExtensions: [],
};

/** The extension of a filename, lowercase and with its dot, or an empty string. */
function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
}

let seq = 0;
const nextLocalId = () => `local-${++seq}`;

/** The error code behind a failed upload, or a flat one when there is nothing to read. */
function uploadErrorCode(err: unknown): string {
  const body = (err as { body?: { error?: unknown } })?.body;
  return typeof body?.error === "string" && body.error ? body.error : "upload_failed";
}

export interface ComposerAttachments {
  files: StagedFile[];
  limits: AttachmentLimits;
  /** Bytes across every row, including the ones still going up. */
  totalBytes: number;
  /** True while any upload is in flight - Send waits for these. */
  busy: boolean;
  /**
   * What a send may name. Only the finished uploads - a half-uploaded file is
   * not a file - and the name travels with the id so the confirmation can list
   * what is going out.
   */
  ready: { id: string; name: string }[];
  add: (picked: FileList | File[] | null) => void;
  /**
   * Stage a picture the media library already holds.
   *
   * Separate from `add` rather than a File built by fetching the URL: the bytes
   * never travel through the browser at all, which is both faster and the only
   * version that works for objects the browser has no right to read.
   */
  addFromMedia: (asset: { id: string; originalFilename: string; sizeBytes: number }) => void;
  remove: (localId: string) => void;
  /** Throw the whole tray away, deleting what was already uploaded. */
  discard: () => void;
  /** Forget the tray WITHOUT deleting: the message has taken the files. */
  clear: () => void;
}

export function useComposerAttachments(
  /** Files already staged for a draft being continued: they start out done. */
  initial?: { id: string; name: string; size: number }[],
): ComposerAttachments {
  const [files, setFiles] = useState<StagedFile[]>(() =>
    (initial ?? []).map((f) => ({ localId: nextLocalId(), id: f.id, name: f.name, size: f.size, status: "done" as const })),
  );
  const [limits, setLimits] = useState<AttachmentLimits>(FALLBACK_LIMITS);

  // Mirrored into a ref so `discard` can read the current list without being
  // rebuilt on every keystroke-triggered render the window does anyway.
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    let alive = true;
    getAttachmentLimits()
      .then((l) => { if (alive) setLimits(l); })
      // A failure leaves the fallback in place. The server enforces the real
      // caps regardless, so the only thing lost is the early refusal.
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);

  /**
   * Take a pick from the file input.
   *
   * The rows are worked out and the uploads started OUTSIDE `setFiles`, against
   * the ref rather than against the updater's `prev`. That is not a style
   * preference: React's StrictMode invokes an updater twice to prove it is pure,
   * and a version of this that started uploads from inside it sent every picked
   * file twice - two rows on the server, one of them orphaned from the moment it
   * was created. An updater must only compute the next state.
   */
  const add = useCallback((picked: FileList | File[] | null) => {
    const chosen = picked ? Array.from(picked) : [];
    if (chosen.length === 0) return;

    // A refused row occupies the tray but not the message: it has no bytes on
    // the server and will never be sent. Counting it against the caps would let
    // one 40MB mistake block four files that would have fit.
    const live = filesRef.current.filter((f) => f.status !== "error");
    let room = limits.maxFiles - live.length;
    let budget = limits.maxTotalBytes - live.reduce((sum, f) => sum + f.size, 0);
    const rows: StagedFile[] = [];

    for (const file of chosen) {
      // Refused files still get a row. A file that vanishes on being picked
      // reads as a broken button; a row saying why reads as an answer.
      const refuse =
        room <= 0 ? "too_many_attachments"
        : limits.blockedExtensions.includes(extensionOf(file.name)) ? "blocked_type"
        : file.size === 0 ? "empty_file"
        : file.size > limits.maxBytes ? "file_too_large"
        : file.size > budget ? "attachments_too_large"
        : null;
      if (!refuse) {
        budget -= file.size;
        room -= 1;
      }
      rows.push({
        localId: nextLocalId(),
        name: file.name,
        size: file.size,
        status: refuse ? "error" : "uploading",
        ...(refuse ? { error: refuse } : {}),
      });
    }

    // Written straight to the ref as well, so a second pick made before React
    // has re-rendered still counts the first one against the caps.
    filesRef.current = [...filesRef.current, ...rows];
    setFiles((prev) => [...prev, ...rows]);

    rows.forEach((row, i) => {
      if (row.status !== "uploading") return;
      uploadAttachment(chosen[i]!)
        .then((up) => setFiles((cur) =>
          cur.map((f) => (f.localId === row.localId
            ? { ...f, id: up.id, name: up.name, size: up.size, status: "done" as const }
            : f))))
        .catch((err) => setFiles((cur) =>
          cur.map((f) => (f.localId === row.localId
            ? { ...f, status: "error" as const, error: uploadErrorCode(err) }
            : f))));
    });
  }, [limits]);

  /**
   * The media-library twin of `add`, for exactly one file.
   *
   * The caps are checked here too, off the size the library recorded, so a
   * 30MB picture is refused in the tray rather than after a round trip. The
   * server checks the real bytes again - the recorded size is what an uploader
   * wrote down, not what the bucket holds.
   */
  const addFromMedia = useCallback((asset: { id: string; originalFilename: string; sizeBytes: number }) => {
    const live = filesRef.current.filter((f) => f.status !== "error");
    const used = live.reduce((sum, f) => sum + f.size, 0);
    const name = asset.originalFilename || "file";
    const refuse =
      limits.maxFiles - live.length <= 0 ? "too_many_attachments"
      : limits.blockedExtensions.includes(extensionOf(name)) ? "blocked_type"
      : asset.sizeBytes === 0 ? "empty_file"
      : asset.sizeBytes > limits.maxBytes ? "file_too_large"
      : asset.sizeBytes > limits.maxTotalBytes - used ? "attachments_too_large"
      : null;

    const row: StagedFile = {
      localId: nextLocalId(),
      name,
      size: asset.sizeBytes,
      status: refuse ? "error" : "uploading",
      ...(refuse ? { error: refuse } : {}),
    };
    filesRef.current = [...filesRef.current, row];
    setFiles((prev) => [...prev, row]);
    if (refuse) return;

    attachFromMedia(asset.id)
      .then((up) => setFiles((cur) =>
        cur.map((f) => (f.localId === row.localId
          ? { ...f, id: up.id, name: up.name, size: up.size, status: "done" as const }
          : f))))
      .catch((err) => setFiles((cur) =>
        cur.map((f) => (f.localId === row.localId
          ? { ...f, status: "error" as const, error: uploadErrorCode(err) }
          : f))));
  }, [limits]);

  const remove = useCallback((localId: string) => {
    setFiles((prev) => {
      const row = prev.find((f) => f.localId === localId);
      // Best effort, and deliberately not awaited: the row leaves the tray on
      // the click. A delete that fails leaves an unspent upload behind, which
      // the sweep collects; making the operator wait for it would be worse.
      if (row?.id) void deleteAttachment(row.id).catch(() => undefined);
      return prev.filter((f) => f.localId !== localId);
    });
  }, []);

  const discard = useCallback(() => {
    for (const row of filesRef.current) {
      if (row.id) void deleteAttachment(row.id).catch(() => undefined);
    }
    setFiles([]);
  }, []);

  const clear = useCallback(() => setFiles([]), []);

  return {
    files,
    limits,
    // Refused rows are excluded, so the figure under the tray is the weight of
    // the mail rather than the weight of what was clicked.
    totalBytes: files.reduce((sum, f) => (f.status === "error" ? sum : sum + f.size), 0),
    busy: files.some((f) => f.status === "uploading"),
    ready: files.filter((f) => f.status === "done" && f.id).map((f) => ({ id: f.id!, name: f.name })),
    add,
    addFromMedia,
    remove,
    discard,
    clear,
  };
}
